import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { formatTimestamp } from "@/lib/utils";
import {
  fetchLaserMarkSettings,
  LASER_MARK_QUERY_KEY,
  saveLaserMarkSettings,
} from "./laserMarkApi";

const PATHS_HELP =
  "Windows paths on the laser PC. Floor Send writes the CSV, then loads the FI-3C or FI-5A LightBurn file that matches the selected queue line. Save each .lbrn with Job Origin on the left-center of the art so the movable tray center mark lines up with the left end of the marks. Operators should not open or swap files in LightBurn.";

export function LaserMarkPathsSettingsSection() {
  const queryClient = useQueryClient();
  const canEdit = useHubModuleAdmin("manufacturing_management");
  const [fi3cTemplatePath, setFi3cTemplatePath] = useState("");
  const [fi5aTemplatePath, setFi5aTemplatePath] = useState("");
  const [csvPath, setCsvPath] = useState("");

  const settingsQuery = useQuery({
    queryKey: [...LASER_MARK_QUERY_KEY, "settings"],
    queryFn: fetchLaserMarkSettings,
  });

  useEffect(() => {
    if (settingsQuery.data == null) return;
    setFi3cTemplatePath(settingsQuery.data.fi3cTemplatePath);
    setFi5aTemplatePath(settingsQuery.data.fi5aTemplatePath);
    setCsvPath(settingsQuery.data.csvPath);
  }, [
    settingsQuery.data?.fi3cTemplatePath,
    settingsQuery.data?.fi5aTemplatePath,
    settingsQuery.data?.csvPath,
  ]);

  const saveMutation = useMutation({
    mutationFn: () =>
      saveLaserMarkSettings({ fi3cTemplatePath, fi5aTemplatePath, csvPath }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...LASER_MARK_QUERY_KEY, "settings"] });
    },
  });

  const saveError =
    saveMutation.error instanceof ApiError
      ? saveMutation.error.message
      : saveMutation.error instanceof Error
        ? saveMutation.error.message
        : null;
  const loadError =
    settingsQuery.error instanceof ApiError
      ? settingsQuery.error.message
      : settingsQuery.error instanceof Error
        ? settingsQuery.error.message
        : null;
  const updatedAt = settingsQuery.data?.updatedAt ?? null;
  const saveDisabled =
    !canEdit ||
    saveMutation.isPending ||
    fi3cTemplatePath.trim() === "" ||
    fi5aTemplatePath.trim() === "" ||
    csvPath.trim() === "";

  return (
    <HubSettingsSection title="LightBurn paths" hoverHint={PATHS_HELP}>
      {loadError != null ? <p className="text-sm text-rose-700">{loadError}</p> : null}
      <ReadOnlyHoverLayer
        active={!canEdit}
        hoverHint="Manufacturing management administrators can edit LightBurn paths."
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="laser-mark-settings-fi3c-template-path">FI-3C template path</Label>
            <Input
              id="laser-mark-settings-fi3c-template-path"
              value={fi3cTemplatePath}
              disabled={!canEdit}
              onChange={(event) => setFi3cTemplatePath(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="laser-mark-settings-fi5a-template-path">FI-5A template path</Label>
            <Input
              id="laser-mark-settings-fi5a-template-path"
              value={fi5aTemplatePath}
              disabled={!canEdit}
              onChange={(event) => setFi5aTemplatePath(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="laser-mark-settings-csv-path">CSV path</Label>
            <Input
              id="laser-mark-settings-csv-path"
              value={csvPath}
              disabled={!canEdit}
              onChange={(event) => setCsvPath(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              disabled={saveDisabled}
              onClick={() => saveMutation.mutate()}
            >
              Save paths
            </Button>
            {updatedAt != null ? (
              <p className="text-xs text-slate-500">Last saved {formatTimestamp(updatedAt)}</p>
            ) : null}
          </div>
          {saveError != null ? <p className="text-sm text-rose-700">{saveError}</p> : null}
        </div>
      </ReadOnlyHoverLayer>
    </HubSettingsSection>
  );
}
